import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { capitalizeFirstLetter, costFormater } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IInvoiceStatusType, IInvoiceType, IReasonCodeResponseDto } from '@/dtos';
import theme from '@/styles/theme';

import { Invoice } from '../../FinOpsInvoicing/types/invoicing.types';
import { InfoTableValue, PrefundStatus } from '../finops.types';

const COMMA_SEPARATOR = ', ';
const TIME_SEPARATOR = 'T';

export const currentStatus = (status?: IInvoiceStatusType) => {
  if (status === IInvoiceStatusType.REOPEN) {
    return PrefundStatus.Reopen;
  }
  if (status === IInvoiceStatusType.PAYMENT_REQUESTED) {
    return PrefundStatus.PaymentRequested;
  }
  if (status === IInvoiceStatusType.PAYMENT_REJECTED) {
    return PrefundStatus.PaymentRejected;
  }
  if (status === IInvoiceStatusType.PAYMENT_INITIATED) {
    return PrefundStatus.PaymentInitiated;
  }
  if (status === IInvoiceStatusType.PAID) {
    return PrefundStatus.Paid;
  }
  if (status === IInvoiceStatusType.PAYMENT_FAILED) {
    return PrefundStatus.PaymentFailed;
  }
  if (status === IInvoiceStatusType.ACTION_REQUIRED) {
    return PrefundStatus.ActionRequired;
  }
  if (status === IInvoiceStatusType.CANCELED) {
    return PrefundStatus.Canceled;
  }

  return undefined;
};

export const formattedInvoiceType = (type?: string) => {
  if (!type) return '-';
  if (type === IInvoiceType.EOY_PLAN_RECONCILIATION) {
    return 'EOY plan reconciliation';
  }

  return capitalizeFirstLetter(type.toString().split('_').join(' ').toLowerCase());
};

const GET_PREFUND_INVOICE_QUERY_KEY = 'GET_PREFUND_INVOICE_QUERY_KEY';

export const useGetPrefundInvoiceInfo = (invoiceId: string) => {
  const { isLoading, isSuccess, data, refetch } = useQuery(
    [GET_PREFUND_INVOICE_QUERY_KEY, invoiceId],
    () => api.get(API_URL.INVOICES_DETAILS_VIEW_BY_INVOICE_ID(invoiceId)),
    {
      retry: 0,
    }
  );

  const formattedData = useMemo<Invoice>(
    () => ({
      amount: data?.data.amount,
      endedAt: (data?.data.ended_at as unknown as string)?.split(TIME_SEPARATOR)[0],
      createdAt: (data?.data.created_at as unknown as string)?.split(TIME_SEPARATOR)[0],
      invoiceId: `${data?.data.invoice_id || ''}`,
      // Same values; the DTO enum and the @/types enum are distinct TS types.
      invoiceStatusType: data?.data.invoice_status_type as unknown as Invoice['invoiceStatusType'],
      invoiceType: data?.data.invoice_type,
      status: currentStatus(data?.data.invoice_status_type),
      largeOrganizationLogo: data?.data.large_organization_logo,
      organizationName: data?.data.organization_name,
      organizationId: data?.data.organization_id ? `${data?.data.organization_id}` : undefined,
      rejectReasons: (data?.data.reason_codes as unknown as IReasonCodeResponseDto[])?.map(
        (reason) => reason.description || ''
      ),
      description: data?.data.description,
      repullManuallyAllowed: data?.data.repull_manually_allowed,
      accountNumberLast4: data?.data.account_number_last4,
      initialFundsRequest: data?.data.initial_funds_request as unknown as string,
      achMethod: data?.data?.ach_method,
    }),
    [data]
  );

  const formattedInvoiceTable: InfoTableValue[] = useMemo(() => {
    const {
      amount,
      invoiceId: iId,
      invoiceType,
      status,
      initialFundsRequest,
      rejectReasons,
      description,
      accountNumberLast4,
      achMethod,
    } = formattedData;

    const bankAccountValue = () => {
      if (description && accountNumberLast4) {
        return `${description} (${accountNumberLast4})`;
      }
      if (description) {
        return description;
      }
      if (accountNumberLast4) {
        return `(${accountNumberLast4})`;
      }
      return '-';
    };

    return [
      {
        title: 'Invoice number',
        value: iId || invoiceId || '-',
      },
      {
        title: 'Invoice type',
        value: formattedInvoiceType(invoiceType),
      },
      {
        title: 'Method',
        value: achMethod ? capitalizeFirstLetter(achMethod) : '-',
      },
      {
        title: 'Amount',
        value: _.isNumber(amount) ? costFormater(amount, true) : '-',
        isAmount: true,
      },
      {
        title: 'Status',
        value: status,
        isStatus: true,
      },
      ...(status === PrefundStatus.PaymentFailed || status === PrefundStatus.PaymentRejected
        ? [
            {
              title: 'Reject Reason',
              value: rejectReasons?.join(COMMA_SEPARATOR) || '-',
              styles: { color: theme.colors.statusColors.deniedErrorExpired },
            },
          ]
        : []),
      {
        title: 'Initial funds request',
        value: initialFundsRequest ? dayjs(initialFundsRequest).format(DEFAULT_DATE_FORMAT) : '-',
      },
      {
        title: 'Bank account',
        value: bankAccountValue(),
      },
    ];
  }, [formattedData, invoiceId]);

  const isRejected = useMemo(
    () =>
      formattedData.status === PrefundStatus.PaymentRejected ||
      formattedData.status === PrefundStatus.PaymentFailed,
    [formattedData]
  );

  const currentInvoiceType = useMemo(() => {
    switch (formattedData.invoiceType) {
      case IInvoiceType.ORGANIZATION_LEVEL_PREFUND_REPLENISHMENT:
      case IInvoiceType.PLAN_LEVEL_PREFUND_REPLENISHMENT:
        return 'Prefund';
      case IInvoiceType.REPLENISHMENT:
      case IInvoiceType.PLAN_LEVEL_REPLENISHMENT:
        return 'Replenishment';
      case IInvoiceType.ORGANIZATION_LEVEL_CONTRIBUTIONS:
        return 'Contribution';
      default:
        return '-';
    }
  }, [formattedData]);

  return {
    data,
    formattedData,
    formattedInvoiceTable,
    isRejected,
    isLoading,
    isSuccess,
    currentInvoiceType,
    refetch,
  };
};
